/**
 * Символы раздела «Мебель» для плана — по компоненту на элемент каталога.
 *
 * Рисунок задан в собственном viewBox символа и растягивается в габарит объекта
 * (UI.SymbolFrame). Цвет — через currentColor, поэтому символ перекрашивается в цвет
 * типа или выделения. Каталог хранит только данные; как элемент выглядит, знает
 * этот файл. Ключ реестра — id элемента каталога.
 */
import type { ReactNode } from "react";
import { UIKit, type SymbolComponent } from "@/shared/ui";
import { KITCHEN_HOB_AT, KITCHEN_SINK_AT } from "../3d/KitchenModels";

const line = { fill: "none", stroke: "currentColor" } as const;

/** Кровать */
export const BedSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 160 200" {...props}>
		<style>{`
			.bed-frame { fill: #f4f1ea; stroke: currentColor; stroke-width: 3; }
			.bed-wood { fill: #412a0d08; stroke: currentColor; stroke-width: 2; }
			.bed-linen { fill: #fbfaf7; stroke: currentColor; stroke-width: 2; }
			.bed-duvet { fill: #d8d2c1; stroke: currentColor; stroke-width: 2; }
			.bed-detail { fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; }
		`}</style>
		<g>
			<rect className="bed-frame" x="1.5" y="1.5" width="157" height="197" rx="8" />
			<rect className="bed-wood" x="21" y="14" width="118" height="52" rx="5" />
			<rect className="bed-linen" x="21" y="47" width="118" height="136" rx="5" />
			<rect className="bed-linen" x="27" y="23" width="48" height="27" rx="5" />
			<rect className="bed-linen" x="85" y="23" width="48" height="27" rx="5" />
			<path className="bed-detail" d="M23 88Q80 78 137 88M23 176H137" />
			<path className="bed-detail" d="M44 16H116" />
		</g>
	</UIKit.SymbolFrame>
);

/** Диван сверху: сиденье, спинка у нижнего края и два подлокотника */
export const SofaSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2200 900" {...props}>
		<rect
			x="12"
			y="12"
			width="2176"
			height="876"
			rx="120"
			fill="none"
			stroke="currentColor"
			strokeWidth="18"
		/>
		<rect
			x="200"
			y="640"
			width="1800"
			height="240"
			rx="60"
			fill="none"
			stroke="currentColor"
			strokeWidth="14"
		/>
		<rect
			x="12"
			y="60"
			width="190"
			height="820"
			rx="70"
			fill="none"
			stroke="currentColor"
			strokeWidth="14"
		/>
		<rect
			x="1998"
			y="60"
			width="190"
			height="820"
			rx="70"
			fill="none"
			stroke="currentColor"
			strokeWidth="14"
		/>
		<path d="M760 60v580M1440 60v580" stroke="currentColor" strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Тумба под телевизор сверху: корпус и две дверцы */
export const TvStandSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1600 400" {...props}>
		<rect
			x="10"
			y="10"
			width="1580"
			height="380"
			rx="24"
			fill="none"
			stroke="currentColor"
			strokeWidth="16"
		/>
		<path
			d="M800 10v380M420 200h60M1120 200h60"
			stroke="currentColor"
			strokeWidth="10"
			strokeLinecap="round"
		/>
	</UIKit.SymbolFrame>
);

/** Телевизор сверху: тонкая панель с подставкой */
export const TvSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 60" {...props}>
		<rect x="6" y="6" width="1188" height="48" rx="12" fill="currentColor" />
		<rect
			x="400"
			y="20"
			width="400"
			height="20"
			fill="none"
			stroke="currentColor"
			strokeWidth="6"
		/>
	</UIKit.SymbolFrame>
);

/**
 * Приставка сверху — лежащая консоль: белая боковая панель с плавной «талией» поверх
 * чёрного корпуса. Корпус выглядывает из-под панели у переднего и заднего края, спереди
 * (к нижнему краю символа) — два порта. Логотипа нет: это товарный знак.
 */
export const PlaystationSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 390 260" {...props}>
		<rect x="14" y="14" width="362" height="232" rx="14" fill="currentColor" opacity="0.85" />
		<path
			d="M6 22C120 52 270 52 384 12V248C270 208 120 208 6 238Z"
			fill="#ffffff"
			stroke="currentColor"
			strokeWidth="8"
			strokeLinejoin="round"
		/>
		<path d="M60 70C150 88 240 88 330 62" {...line} strokeWidth="4" opacity="0.5" />
		<rect x="40" y="232" width="22" height="8" rx="2" fill="#ffffff" />
		<rect x="72" y="232" width="22" height="8" rx="2" fill="#ffffff" />
	</UIKit.SymbolFrame>
);

/**
 * Кухонный гарнитур сверху: столешница, мойка и варочная панель на тех же долях ширины,
 * что и в 3D; верхние шкафы — пунктиром, как принято на рабочих чертежах. Стена — сверху.
 */
const KITCHEN_W = 2400;
const SINK_X = KITCHEN_W * (0.5 + KITCHEN_SINK_AT);
const HOB_X = KITCHEN_W * (0.5 + KITCHEN_HOB_AT);
export const KitchenSetSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox={`0 0 ${KITCHEN_W} 600`} {...props}>
		<rect x="8" y="8" width={KITCHEN_W - 16} height="584" {...line} strokeWidth="14" />
		<path
			d={`M8 350H${KITCHEN_W - 8}`}
			stroke="currentColor"
			strokeWidth="8"
			strokeDasharray="40 24"
		/>
		<rect x={SINK_X - 250} y="100" width="500" height="440" rx="30" {...line} strokeWidth="10" />
		<rect x={SINK_X - 210} y="140" width="420" height="360" rx="40" {...line} strokeWidth="6" />
		<circle cx={SINK_X} cy="320" r="24" {...line} strokeWidth="6" />
		<rect x={HOB_X - 290} y="60" width="580" height="500" rx="16" {...line} strokeWidth="10" />
		{[
			[-140, -110, 90],
			[140, -110, 70],
			[-140, 120, 70],
			[140, 120, 90],
		].map(([dx, dy, r]) => (
			<circle key={`${dx}${dy}`} cx={HOB_X + dx} cy={310 + dy} r={r} {...line} strokeWidth="8" />
		))}
	</UIKit.SymbolFrame>
);

/** Холодильник сверху: корпус, дверца спереди и ручка у правого края */
export const FridgeSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 650" {...props}>
		<rect x="8" y="8" width="584" height="634" rx="12" {...line} strokeWidth="14" />
		<path d="M8 600h584" stroke="currentColor" strokeWidth="10" />
		<path d="M540 610v30" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
		<path d="M40 40l520 520M560 40L40 560" stroke="currentColor" strokeWidth="4" opacity="0.5" />
	</UIKit.SymbolFrame>
);

/** Обеденный стол сверху: столешница и ножки по углам */
export const DiningTableSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 800" {...props}>
		<rect x="8" y="8" width="1184" height="784" rx="24" {...line} strokeWidth="14" />
		{[
			[85, 85],
			[1115, 85],
			[85, 715],
			[1115, 715],
		].map(([x, y]) => (
			<rect
				key={`${x}${y}`}
				x={x - 25}
				y={y - 25}
				width="50"
				height="50"
				{...line}
				strokeWidth="6"
				strokeDasharray="12 8"
			/>
		))}
	</UIKit.SymbolFrame>
);

/** Стул сверху: сиденье и спинка у верхнего края */
export const ChairSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 500" {...props}>
		<rect x="8" y="50" width="434" height="442" rx="30" {...line} strokeWidth="14" />
		<rect x="8" y="8" width="434" height="46" rx="16" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Компьютерный стол сверху: столешница, тумба с ящиками справа — пунктиром под ней */
export const DeskSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 700" {...props}>
		<rect x="8" y="8" width="1384" height="684" rx="10" {...line} strokeWidth="14" />
		<rect
			x="970"
			y="30"
			width="410"
			height="650"
			{...line}
			strokeWidth="8"
			strokeDasharray="30 18"
		/>
		<path d="M30 30v640" stroke="currentColor" strokeWidth="8" strokeDasharray="30 18" />
	</UIKit.SymbolFrame>
);

/** Офисное кресло сверху: пятилучевая крестовина, сиденье и спинка сзади */
export const OfficeChairSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 620 620" {...props}>
		{[0, 72, 144, 216, 288].map((angle) => (
			<path
				key={angle}
				d="M310 310v-290"
				stroke="currentColor"
				strokeWidth="10"
				strokeLinecap="round"
				opacity="0.5"
				transform={`rotate(${angle + 180} 310 310)`}
			/>
		))}
		<rect x="70" y="120" width="480" height="440" rx="90" {...line} strokeWidth="14" />
		<path d="M110 90Q310 20 510 90" {...line} strokeWidth="40" strokeLinecap="round" />
		<path
			d="M50 200v240M570 200v240"
			stroke="currentColor"
			strokeWidth="24"
			strokeLinecap="round"
		/>
	</UIKit.SymbolFrame>
);

/** Пуф сверху: круг, кант и пуговица в центре */
export const PoufSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 450" {...props}>
		<circle cx="225" cy="225" r="215" {...line} strokeWidth="14" />
		<circle cx="225" cy="225" r="180" {...line} strokeWidth="6" strokeDasharray="20 14" />
		<circle cx="225" cy="225" r="18" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Настенный телевизор: сверху — тонкая панель вдоль стены, спереди — рамка и экран */
export const TvWallSymbol: SymbolComponent = ({ facing = "plan", ...props }) =>
	facing === "front" ? (
		<UIKit.SymbolFrame viewBox="0 0 1230 710" {...props}>
			<rect x="8" y="8" width="1214" height="694" rx="10" {...line} strokeWidth="16" />
			<rect x="30" y="30" width="1170" height="650" fill="currentColor" opacity="0.15" />
		</UIKit.SymbolFrame>
	) : (
		<UIKit.SymbolFrame viewBox="0 0 1230 50" {...props}>
			<rect x="0" y="0" width="1230" height="30" rx="6" fill="currentColor" />
			<rect x="430" y="30" width="370" height="20" fill="currentColor" opacity="0.5" />
		</UIKit.SymbolFrame>
	);

/** Диван сверху передом вниз: спинка у верхнего края, подлокотники и подушки сиденья */
export const SofaFrontSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1700 780" {...props}>
		<rect x="10" y="10" width="1680" height="760" rx="90" {...line} strokeWidth="16" />
		<rect x="170" y="10" width="1360" height="200" rx="50" {...line} strokeWidth="12" />
		<rect x="10" y="10" width="170" height="760" rx="70" {...line} strokeWidth="12" />
		<rect x="1520" y="10" width="170" height="760" rx="70" {...line} strokeWidth="12" />
		<path d="M850 210v550" stroke="currentColor" strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Угловой диван сверху: спинка у верхнего края, шезлонг справа уходит к нижнему */
export const CornerSofaSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2800 1700" {...props}>
		<path d="M10 10H2790V1690H1900V940H10Z" {...line} strokeWidth="16" strokeLinejoin="round" />
		<rect x="10" y="10" width="2780" height="200" rx="50" {...line} strokeWidth="12" />
		<rect x="10" y="10" width="180" height="930" rx="60" {...line} strokeWidth="12" />
		<rect x="2610" y="10" width="180" height="1680" rx="60" {...line} strokeWidth="12" />
		<path d="M1045 210v730M1900 210v730" stroke="currentColor" strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Угловой диван с шезлонгом слева: зеркало `CornerSofaSymbol` */
export const CornerSofaLeftSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2800 1700" {...props}>
		<path d="M2790 10H10V1690H900V940H2790Z" {...line} strokeWidth="16" strokeLinejoin="round" />
		<rect x="10" y="10" width="2780" height="200" rx="50" {...line} strokeWidth="12" />
		<rect x="2610" y="10" width="180" height="930" rx="60" {...line} strokeWidth="12" />
		<rect x="10" y="10" width="180" height="1680" rx="60" {...line} strokeWidth="12" />
		<path d="M1755 210v730M900 210v730" stroke="currentColor" strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Полукруглый диван сверху: дуга спинки сверху, сиденье смотрит внутрь к нижнему краю */
export const CurvedSofaSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 3200 1890" {...props}>
		<path
			d="M10 1880A1590 1870 0 0 1 3190 1880H2300A700 980 0 0 0 900 1880Z"
			{...line}
			strokeWidth="16"
			strokeLinejoin="round"
		/>
		<path d="M210 1880A1390 1670 0 0 1 2990 1880" {...line} strokeWidth="12" />
	</UIKit.SymbolFrame>
);

/** Кресло сверху: спинка сверху, подлокотники, подушка сиденья */
export const ArmchairSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 800 780" {...props}>
		<rect x="10" y="10" width="780" height="760" rx="120" {...line} strokeWidth="14" />
		<path d="M110 200Q400 120 690 200" {...line} strokeWidth="14" />
		<rect x="150" y="240" width="500" height="480" rx="60" {...line} strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Кресло-мешок сверху: мягкий овал и вмятина сиденья */
export const BeanbagSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 800 800" {...props}>
		<ellipse cx="400" cy="400" rx="385" ry="385" {...line} strokeWidth="14" />
		<ellipse
			cx="400"
			cy="470"
			rx="220"
			ry="190"
			{...line}
			strokeWidth="10"
			strokeDasharray="30 20"
		/>
	</UIKit.SymbolFrame>
);

/** Журнальный столик сверху: столешница со скруглёнными углами и ножки пунктиром */
export const CoffeeTableSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 500" {...props}>
		<rect x="8" y="8" width="984" height="484" rx="40" {...line} strokeWidth="14" />
		<rect
			x="60"
			y="60"
			width="880"
			height="380"
			rx="20"
			{...line}
			strokeWidth="6"
			strokeDasharray="24 16"
		/>
	</UIKit.SymbolFrame>
);

/** Стеллаж сверху: корпус у стены, полки и корешки книг */
export const BookshelfSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 420" {...props}>
		<rect x="8" y="8" width="984" height="404" {...line} strokeWidth="14" />
		<path d="M340 8v404M660 8v404" stroke="currentColor" strokeWidth="10" />
		{[60, 110, 160, 210, 400, 450, 500, 560, 720, 770, 830, 880].map((x) => (
			<path
				key={x}
				d={`M${x} 40v${x % 3 ? 300 : 260}`}
				stroke="currentColor"
				strokeWidth="14"
				opacity="0.5"
			/>
		))}
	</UIKit.SymbolFrame>
);

/** Торшер сверху: круг абажура, стойка и основание */
export const FloorLampSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 700 700" {...props}>
		<circle cx="350" cy="350" r="340" {...line} strokeWidth="12" />
		<circle cx="350" cy="350" r="200" {...line} strokeWidth="8" strokeDasharray="24 16" />
		<circle cx="350" cy="350" r="26" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Пианино сверху: корпус у стены, клавиатура выступает вперёд — к нижнему краю */
export const PianoSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1500 740" {...props}>
		<rect x="10" y="10" width="1480" height="420" rx="16" fill="currentColor" opacity="0.85" />
		<rect x="60" y="430" width="1380" height="300" rx="10" {...line} strokeWidth="12" />
		<path
			d={Array.from(
				{ length: 27 },
				(_, i) => `M${110 + i * 50} 440v${i % 7 === 2 || i % 7 === 6 ? 280 : 170}`,
			).join("")}
			stroke="currentColor"
			strokeWidth="6"
		/>
	</UIKit.SymbolFrame>
);

/** Прикроватная тумба сверху: корпус и ящик спереди */
export const NightstandSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 500 400" {...props}>
		<rect x="8" y="8" width="484" height="384" rx="16" {...line} strokeWidth="14" />
		<path d="M8 330h484" stroke="currentColor" strokeWidth="8" />
		<path d="M200 362h100" stroke="currentColor" strokeWidth="14" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Круглый стол сверху: столешница и опора пунктиром */
export const RoundTableSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 1200" {...props}>
		<circle cx="600" cy="600" r="590" {...line} strokeWidth="16" />
		<circle cx="600" cy="600" r="260" {...line} strokeWidth="8" strokeDasharray="30 20" />
	</UIKit.SymbolFrame>
);

/** Кофемашина сверху: корпус, решётка для чашек и рожок спереди */
export const CoffeeMachineSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 260 400" {...props}>
		<rect x="6" y="6" width="248" height="388" rx="20" {...line} strokeWidth="10" />
		<rect x="40" y="40" width="180" height="140" rx="10" {...line} strokeWidth="6" />
		<circle cx="130" cy="290" r="44" {...line} strokeWidth="8" />
		<path d="M130 334v58" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Велотренажёр сверху: рама вдоль, маховик спереди, седло и руль */
export const ExerciseBikeSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 520" {...props}>
		<path
			d="M60 460h1080M60 60h1080"
			stroke="currentColor"
			strokeWidth="14"
			strokeLinecap="round"
			opacity="0.5"
		/>
		<rect x="160" y="200" width="880" height="120" rx="60" {...line} strokeWidth="14" />
		<rect x="880" y="160" width="220" height="200" rx="40" fill="currentColor" opacity="0.8" />
		<ellipse cx="330" cy="260" rx="110" ry="70" {...line} strokeWidth="12" />
		<path d="M760 110v300" stroke="currentColor" strokeWidth="18" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Ноутбук сверху: откинутая крышка сзади, клавиатура и тачпад */
export const LaptopSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 320 280" {...props}>
		<rect x="4" y="4" width="312" height="40" rx="6" fill="currentColor" opacity="0.5" />
		<rect x="4" y="50" width="312" height="226" rx="10" {...line} strokeWidth="6" />
		<rect x="24" y="66" width="272" height="100" {...line} strokeWidth="4" />
		<path
			d="M24 100h272M24 133h272M92 66v100M160 66v100M228 66v100"
			stroke="currentColor"
			strokeWidth="2"
		/>
		<rect x="112" y="186" width="96" height="66" rx="6" {...line} strokeWidth="4" />
	</UIKit.SymbolFrame>
);

/** Монитор сверху: панель ближе к стене и подставка под ней */
export const MonitorSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 615 200" {...props}>
		<rect x="4" y="56" width="607" height="28" rx="6" fill="currentColor" />
		<rect x="185" y="6" width="245" height="188" rx="16" {...line} strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Кровать боком, длинной стороной вдоль стены: каркас, матрас и подушка у левого края */
const SideBed = ({ children }: { children?: ReactNode }) => (
	<>
		<rect x="10" y="10" width="2030" height="1210" rx="20" {...line} strokeWidth="16" />
		<rect x="60" y="60" width="1930" height="1110" rx="30" {...line} strokeWidth="8" />
		<rect x="100" y="220" width="300" height="790" rx="60" {...line} strokeWidth="8" />
		{children}
	</>
);

/** Кровать-чердак сверху: спальное место, под ним пунктиром стол у переднего края */
export const LoftBedSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2050 1230" {...props}>
		<SideBed>
			<rect
				x="600"
				y="700"
				width="1200"
				height="500"
				{...line}
				strokeWidth="8"
				strokeDasharray="40 24"
			/>
		</SideBed>
	</UIKit.SymbolFrame>
);

/** Кровать-домик сверху: низкий матрас и конёк крыши по оси */
export const HouseBedSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2050 1230" {...props}>
		<SideBed>
			<path d="M10 615H2040" stroke="currentColor" strokeWidth="10" strokeDasharray="50 30" />
		</SideBed>
	</UIKit.SymbolFrame>
);

/** Детская кроватка сверху: борта из реек и матрас */
export const CribSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1250 790" {...props}>
		<rect x="10" y="10" width="1230" height="770" rx="16" {...line} strokeWidth="16" />
		<rect x="70" y="70" width="1110" height="650" rx="20" {...line} strokeWidth="6" />
		<path
			d={Array.from({ length: 14 }, (_, i) => `M${130 + i * 76} 10v40M${130 + i * 76} 740v40`).join(
				"",
			)}
			stroke="currentColor"
			strokeWidth="10"
		/>
	</UIKit.SymbolFrame>
);

/** Комод сверху: корпус, ящики выдвигаются вперёд — линия фасада у нижнего края */
export const DresserSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 480" {...props}>
		<rect x="8" y="8" width="884" height="464" rx="12" {...line} strokeWidth="14" />
		<path d="M8 420h884" stroke="currentColor" strokeWidth="8" />
		<path
			d="M200 446h100M600 446h100"
			stroke="currentColor"
			strokeWidth="14"
			strokeLinecap="round"
		/>
	</UIKit.SymbolFrame>
);

/** Пеленальный комод сверху: корпус и матрасик с бортиками */
export const ChangingTableSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 685" {...props}>
		<rect x="8" y="8" width="884" height="669" rx="12" {...line} strokeWidth="14" />
		<rect x="60" y="50" width="780" height="560" rx="60" {...line} strokeWidth="10" />
		<rect x="110" y="100" width="680" height="460" rx="40" {...line} strokeWidth="6" />
	</UIKit.SymbolFrame>
);

/** Туалетный столик сверху: столешница, зеркало у стены и пуф перед столиком */
export const DressingTableSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 650" {...props}>
		<rect x="8" y="8" width="984" height="420" rx="16" {...line} strokeWidth="14" />
		<path d="M250 40h500" stroke="currentColor" strokeWidth="24" strokeLinecap="round" />
		<circle cx="500" cy="500" r="140" {...line} strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Распашной шкаф сверху: корпус и двери, открывающиеся дугами вперёд — как на чертеже */
export const WardrobeSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 600" {...props}>
		<rect x="8" y="8" width="1184" height="584" {...line} strokeWidth="14" />
		<path d="M8 540h1184M600 540v52" stroke="currentColor" strokeWidth="8" />
		<path d="M40 40l1120 500M1160 40L40 540" stroke="currentColor" strokeWidth="4" opacity="0.4" />
	</UIKit.SymbolFrame>
);

/** Шкаф-купе сверху: корпус и двери-купе на двух направляющих, внахлёст */
export const SlidingWardrobeSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2000 650" {...props}>
		<rect x="8" y="8" width="1984" height="634" {...line} strokeWidth="14" />
		<path
			d="M30 560h680M650 600h700M1290 560h680"
			stroke="currentColor"
			strokeWidth="16"
			strokeLinecap="round"
		/>
		<path d="M40 40l1920 480M1960 40L40 520" stroke="currentColor" strokeWidth="4" opacity="0.4" />
	</UIKit.SymbolFrame>
);

/** Стеллаж из ячеек сверху: корпус и вертикальные перегородки */
export const ShelfSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 390" {...props}>
		<rect x="8" y="8" width="1184" height="374" {...line} strokeWidth="14" />
		<path d="M300 8v374M600 8v374M900 8v374" stroke="currentColor" strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Витрина сверху: рама и стеклянный фасад тонкой линией */
export const DisplayCabinetSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 450" {...props}>
		<rect x="8" y="8" width="1184" height="434" {...line} strokeWidth="14" />
		<path d="M30 410h1140" stroke="currentColor" strokeWidth="4" />
		<path d="M600 380v60" stroke="currentColor" strokeWidth="10" />
		<path d="M40 40h1120M40 200h1120" stroke="currentColor" strokeWidth="4" opacity="0.4" />
	</UIKit.SymbolFrame>
);

/** Стол сверху: столешница и ножки пунктиром — письменный, игровой, с подъёмом */
export const PlainDeskSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 700" {...props}>
		<rect x="8" y="8" width="1384" height="684" rx="10" {...line} strokeWidth="14" />
		<path
			d="M90 60v580M1310 60v580"
			stroke="currentColor"
			strokeWidth="10"
			strokeDasharray="30 18"
		/>
	</UIKit.SymbolFrame>
);

/** Угловой стол сверху: Г-образная столешница, длинная сторона у стены, крыло справа к низу */
export const CornerDeskSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 1400" {...props}>
		<path d="M8 8H1392V1392H800V600H8Z" {...line} strokeWidth="14" strokeLinejoin="round" />
		<path d="M800 600H1392" stroke="currentColor" strokeWidth="6" strokeDasharray="30 18" />
	</UIKit.SymbolFrame>
);

/** Игровое место сверху: стол, два монитора у стены, клавиатура и системный блок справа */
export const GamingSetupSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1600 815" {...props}>
		<rect x="8" y="8" width="1584" height="799" rx="10" {...line} strokeWidth="14" />
		<path
			d="M200 140L700 100M760 100L1260 140"
			stroke="currentColor"
			strokeWidth="30"
			strokeLinecap="round"
		/>
		<rect x="480" y="480" width="560" height="160" rx="16" {...line} strokeWidth="8" />
		<rect x="1340" y="80" width="200" height="420" rx="16" fill="currentColor" opacity="0.6" />
	</UIKit.SymbolFrame>
);

/** Системный блок сверху: корпус, стеклянная стенка слева и вентиляторы спереди */
export const PcSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 230 480" {...props}>
		<rect x="6" y="6" width="218" height="468" rx="12" fill="currentColor" opacity="0.85" />
		<path d="M20 20v440" stroke="#ffffff" strokeWidth="6" opacity="0.6" />
		<path d="M50 450h130" stroke="#ffffff" strokeWidth="10" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Приставка стоя сверху: узкий корпус и два геймпада перед ней */
export const ConsoleStandSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 440 235" {...props}>
		<rect x="160" y="6" width="120" height="200" rx="40" {...line} strokeWidth="10" />
		<ellipse cx="70" cy="180" rx="60" ry="40" {...line} strokeWidth="8" />
		<ellipse cx="370" cy="180" rx="60" ry="40" {...line} strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Проигрыватель сверху: корпус, пластинка и тонарм справа */
export const TurntableSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 490" {...props}>
		<rect x="8" y="8" width="984" height="474" rx="16" {...line} strokeWidth="14" />
		<circle cx="430" cy="245" r="190" {...line} strokeWidth="10" />
		<circle cx="430" cy="245" r="40" fill="currentColor" />
		<path
			d="M820 80V300L640 360"
			{...line}
			strokeWidth="14"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</UIKit.SymbolFrame>
);

/** Колонка сверху: корпус и динамик, обращённый вперёд */
export const SpeakerSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 375 375" {...props}>
		<rect x="8" y="8" width="359" height="359" rx="16" {...line} strokeWidth="14" />
		<path d="M60 340h255" stroke="currentColor" strokeWidth="18" strokeLinecap="round" />
		<circle cx="187" cy="187" r="40" fill="currentColor" opacity="0.5" />
	</UIKit.SymbolFrame>
);

/** Робот-пылесос сверху: круглый корпус, бампер спереди и датчик */
export const RobotVacuumSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 350 350" {...props}>
		<circle cx="175" cy="175" r="165" {...line} strokeWidth="14" />
		<path d="M60 270A150 150 0 0 0 290 270" {...line} strokeWidth="10" />
		<circle cx="175" cy="130" r="36" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Барный стул сверху: круглое сиденье и раскинутые ножки по углам */
export const BarStoolSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 490 490" {...props}>
		<rect
			x="20"
			y="20"
			width="450"
			height="450"
			rx="20"
			{...line}
			strokeWidth="6"
			strokeDasharray="20 14"
		/>
		<circle cx="245" cy="245" r="170" {...line} strokeWidth="14" />
	</UIKit.SymbolFrame>
);

/** Барный стол со стульями сверху: столешница у стены и два круглых сиденья впереди */
export const BarSetSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 1030" {...props}>
		<rect x="8" y="8" width="1384" height="560" rx="16" {...line} strokeWidth="14" />
		<circle cx="420" cy="820" r="170" {...line} strokeWidth="12" />
		<circle cx="980" cy="820" r="170" {...line} strokeWidth="12" />
	</UIKit.SymbolFrame>
);

/** Кухонный остров сверху: шкафы и столешница со свесом для сидения к нижнему краю */
export const KitchenIslandSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1800 1000" {...props}>
		<rect x="8" y="8" width="1784" height="984" rx="12" {...line} strokeWidth="14" />
		<path d="M8 650h1784" stroke="currentColor" strokeWidth="8" strokeDasharray="40 24" />
		<rect x="1250" y="120" width="420" height="380" rx="30" {...line} strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Холодильник Side-by-Side сверху: корпус и две двери спереди */
export const SideBySideFridgeSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 700" {...props}>
		<rect x="8" y="8" width="884" height="684" rx="12" {...line} strokeWidth="14" />
		<path d="M8 640h884M450 640v52" stroke="currentColor" strokeWidth="10" />
		<path d="M40 40l820 580M860 40L40 620" stroke="currentColor" strokeWidth="4" opacity="0.5" />
	</UIKit.SymbolFrame>
);

/** Микроволновка сверху: корпус, дверца спереди и панель справа */
export const MicrowaveSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 500 410" {...props}>
		<rect x="6" y="6" width="488" height="398" rx="14" {...line} strokeWidth="12" />
		<path d="M6 370h488M370 370v34" stroke="currentColor" strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Лошадка-качалка сверху: два полоза вдоль и корпус посередине */
export const RockingHorseSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 370 900" {...props}>
		<path d="M40 20V880M330 20V880" stroke="currentColor" strokeWidth="18" strokeLinecap="round" />
		<rect x="120" y="180" width="130" height="560" rx="60" {...line} strokeWidth="12" />
		<ellipse cx="185" cy="820" rx="50" ry="70" fill="currentColor" opacity="0.6" />
	</UIKit.SymbolFrame>
);

/** Вигвам сверху: основание и шесты, сходящиеся в центре */
export const TeepeeSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1300 990" {...props}>
		<rect x="10" y="10" width="1280" height="970" rx="20" {...line} strokeWidth="14" />
		<path d="M10 10L1290 980M1290 10L10 980" stroke="currentColor" strokeWidth="10" />
		<circle cx="650" cy="495" r="40" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Беговая дорожка сверху: полотно вдоль, пульт с поручнями у дальнего края */
export const TreadmillSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 970 1340" {...props}>
		<rect x="60" y="200" width="850" height="1130" rx="40" {...line} strokeWidth="14" />
		<rect x="200" y="300" width="570" height="980" rx="20" {...line} strokeWidth="8" />
		<rect x="10" y="10" width="950" height="190" rx="30" fill="currentColor" opacity="0.7" />
	</UIKit.SymbolFrame>
);

/** Груша на стойке сверху: тренога-основание и круглая груша на кронштейне */
export const PunchingBagSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1430 640" {...props}>
		<path
			d="M20 40L400 320L20 600M400 320H1100"
			{...line}
			strokeWidth="18"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<circle cx="1100" cy="320" r="200" {...line} strokeWidth="14" />
	</UIKit.SymbolFrame>
);

/** Шведская стенка сверху: стойки у стены и перекладины, турник выступает вперёд */
export const WallBarsSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1100 360" {...props}>
		<rect x="8" y="8" width="1084" height="120" {...line} strokeWidth="14" />
		<path d="M60 128V340M1040 128V340M60 340H1040" stroke="currentColor" strokeWidth="14" />
	</UIKit.SymbolFrame>
);

/** Торшер-дуга сверху: тяжёлое основание слева, дуга и купол плафона справа */
export const ArcLampSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1480 430" {...props}>
		<circle cx="215" cy="215" r="200" {...line} strokeWidth="14" />
		<path d="M215 215H1250" stroke="currentColor" strokeWidth="14" strokeDasharray="40 24" />
		<circle cx="1265" cy="215" r="200" fill="currentColor" opacity="0.5" />
	</UIKit.SymbolFrame>
);

/** Пара круглых столиков сверху: больший и задвинутый под него меньший */
export const NestingTablesSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 760" {...props}>
		<circle cx="380" cy="380" r="370" {...line} strokeWidth="14" />
		<circle cx="700" cy="440" r="290" {...line} strokeWidth="10" strokeDasharray="30 18" />
	</UIKit.SymbolFrame>
);

/** Гребной тренажёр сверху: рельс вдоль, сиденье, маховик у дальнего края */
export const RowingMachineSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 830 1800" {...props}>
		<path d="M415 300V1780" stroke="currentColor" strokeWidth="40" strokeLinecap="round" />
		<rect x="215" y="20" width="400" height="300" rx="60" fill="currentColor" opacity="0.7" />
		<rect x="265" y="1100" width="300" height="220" rx="40" {...line} strokeWidth="14" />
		<path d="M60 600H770" stroke="currentColor" strokeWidth="18" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Скамья для жима сверху: узкая скамья вдоль, поднятая спинка и стойки у изголовья */
export const WeightBenchSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 520 1300" {...props}>
		<rect x="140" y="40" width="240" height="1220" rx="40" {...line} strokeWidth="14" />
		<path d="M140 560H380" stroke="currentColor" strokeWidth="10" />
		<path d="M30 140H490" stroke="currentColor" strokeWidth="18" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Стойка с гантелями сверху: рама и пары гантелей в ряд */
export const DumbbellRackSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 800 335" {...props}>
		<rect x="8" y="8" width="784" height="319" rx="16" {...line} strokeWidth="12" />
		{[110, 250, 400, 550, 690].map((x) => (
			<path
				key={x}
				d={`M${x} 70V265`}
				stroke="currentColor"
				strokeWidth="60"
				strokeLinecap="round"
				opacity="0.6"
			/>
		))}
	</UIKit.SymbolFrame>
);

/** Велосипед сверху: колёса по краям, рама, руль поперёк и седло */
export const BicycleSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1750 490" {...props}>
		<path
			d="M20 245H560M1190 245H1730"
			stroke="currentColor"
			strokeWidth="40"
			strokeLinecap="round"
		/>
		<path d="M560 245H1190" stroke="currentColor" strokeWidth="18" />
		<path d="M1300 40V450" stroke="currentColor" strokeWidth="24" strokeLinecap="round" />
		<ellipse cx="640" cy="245" rx="110" ry="60" fill="currentColor" opacity="0.7" />
	</UIKit.SymbolFrame>
);

/** Очиститель воздуха сверху: круглый корпус и решётка выхода */
export const AirPurifierSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="46" {...line} strokeWidth="5" />
		<circle cx="50" cy="50" r="30" {...line} strokeWidth="3" strokeDasharray="4 4" />
		<circle cx="50" cy="50" r="8" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Саундбар сверху: длинная узкая панель, решётка — к нижнему краю */
export const SoundbarSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 150" {...props}>
		<rect x="6" y="6" width="888" height="138" rx="60" fill="currentColor" opacity="0.85" />
		<path d="M80 110H820" stroke="#ffffff" strokeWidth="8" strokeDasharray="6 10" opacity="0.6" />
	</UIKit.SymbolFrame>
);

/** Кондиционер: сверху — корпус вдоль стены, спереди — корпус и жалюзи у нижнего края */
export const AirConditionerSymbol: SymbolComponent = ({ facing = "plan", ...props }) =>
	facing === "front" ? (
		<UIKit.SymbolFrame viewBox="0 0 900 360" {...props}>
			<rect x="8" y="8" width="884" height="344" rx="60" {...line} strokeWidth="14" />
			<path d="M60 270H840M60 310H840" stroke="currentColor" strokeWidth="10" />
		</UIKit.SymbolFrame>
	) : (
		<UIKit.SymbolFrame viewBox="0 0 900 300" {...props}>
			<rect x="8" y="8" width="884" height="284" rx="60" {...line} strokeWidth="14" />
			<path d="M60 230H840" stroke="currentColor" strokeWidth="10" strokeDasharray="30 18" />
		</UIKit.SymbolFrame>
	);

/** Вытяжка: сверху — купол и короб у стены, спереди — трапеция купола и короб вверх */
export const RangeHoodSymbol: SymbolComponent = ({ facing = "plan", ...props }) =>
	facing === "front" ? (
		<UIKit.SymbolFrame viewBox="0 0 720 800" {...props}>
			<path d="M10 790L260 560H460L710 790Z" {...line} strokeWidth="14" strokeLinejoin="round" />
			<rect x="260" y="10" width="200" height="550" {...line} strokeWidth="14" />
		</UIKit.SymbolFrame>
	) : (
		<UIKit.SymbolFrame viewBox="0 0 720 370" {...props}>
			<rect x="10" y="10" width="700" height="350" rx="12" {...line} strokeWidth="14" />
			<rect x="260" y="10" width="200" height="140" {...line} strokeWidth="10" />
			<path
				d="M10 360L260 150M710 360L460 150"
				stroke="currentColor"
				strokeWidth="6"
				opacity="0.5"
			/>
		</UIKit.SymbolFrame>
	);

/** Плита сверху: четыре конфорки, ручки спереди */
export const StoveSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 650 600" {...props}>
		<rect x="8" y="8" width="634" height="584" rx="12" {...line} strokeWidth="14" />
		{[
			[180, 180, 90],
			[470, 180, 70],
			[180, 420, 70],
			[470, 420, 90],
		].map(([cx, cy, r]) => (
			<circle key={`${cx}${cy}`} cx={cx} cy={cy} r={r} {...line} strokeWidth="10" />
		))}
		<path d="M8 560H642" stroke="currentColor" strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Посудомойка сверху: корпус и дверца спереди с ручкой */
export const DishwasherSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 600" {...props}>
		<rect x="8" y="8" width="584" height="584" rx="12" {...line} strokeWidth="14" />
		<path d="M8 550H592" stroke="currentColor" strokeWidth="8" />
		<path d="M150 575H450" stroke="currentColor" strokeWidth="14" strokeLinecap="round" />
		<circle cx="300" cy="280" r="120" {...line} strokeWidth="6" strokeDasharray="20 14" />
	</UIKit.SymbolFrame>
);

/** Ведро с педалью сверху: корпус, крышка и педаль спереди */
export const TrashBinSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 400 320" {...props}>
		<rect x="8" y="8" width="384" height="290" rx="60" {...line} strokeWidth="12" />
		<path d="M60 60H340" stroke="currentColor" strokeWidth="8" />
		<rect x="150" y="290" width="100" height="28" rx="8" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Чайник и тостер сверху: круглый чайник слева, тостер с двумя щелями справа */
export const KettleToasterSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 330" {...props}>
		<circle cx="110" cy="165" r="95" {...line} strokeWidth="10" />
		<rect x="240" y="60" width="200" height="210" rx="30" {...line} strokeWidth="10" />
		<path
			d="M290 100V230M390 100V230"
			stroke="currentColor"
			strokeWidth="16"
			strokeLinecap="round"
		/>
	</UIKit.SymbolFrame>
);

/** Детский столик сверху: квадратная столешница и стульчики по бокам */
export const KidsTableSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 630" {...props}>
		<rect x="250" y="60" width="500" height="510" rx="20" {...line} strokeWidth="14" />
		<rect x="20" y="170" width="200" height="290" rx="30" {...line} strokeWidth="10" />
		<rect x="780" y="170" width="200" height="290" rx="30" {...line} strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Стульчик для кормления сверху: сиденье, спинка сзади и поднос впереди */
export const HighChairSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 585" {...props}>
		<rect
			x="40"
			y="20"
			width="520"
			height="545"
			rx="40"
			{...line}
			strokeWidth="6"
			strokeDasharray="20 14"
		/>
		<rect x="150" y="60" width="300" height="320" rx="40" {...line} strokeWidth="12" />
		<path d="M150 60H450" stroke="currentColor" strokeWidth="30" />
		<rect x="90" y="400" width="420" height="140" rx="50" fill="currentColor" opacity="0.5" />
	</UIKit.SymbolFrame>
);

/** Банкетка сверху: мягкое сиденье и ножки по углам */
export const BenchSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 420" {...props}>
		<rect x="10" y="10" width="1180" height="400" rx="60" {...line} strokeWidth="14" />
		<rect
			x="60"
			y="60"
			width="1080"
			height="300"
			rx="30"
			{...line}
			strokeWidth="6"
			strokeDasharray="30 18"
		/>
	</UIKit.SymbolFrame>
);

/** Скамья со спинкой сверху: сиденье и спинка у верхнего края */
export const BenchBackSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 490" {...props}>
		<rect x="10" y="90" width="1380" height="390" rx="20" {...line} strokeWidth="14" />
		<rect x="10" y="10" width="1380" height="70" rx="20" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Вешалка-стойка сверху: тренога и крючки по кругу */
export const CoatStandSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<path
			d="M50 50L50 6M50 50L12 72M50 50L88 72"
			stroke="currentColor"
			strokeWidth="5"
			strokeLinecap="round"
		/>
		<circle cx="50" cy="50" r="22" {...line} strokeWidth="3" strokeDasharray="5 4" />
		<circle cx="50" cy="50" r="6" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Напольная вешалка сверху: рейл вдоль, плечики поперёк, полка для обуви пунктиром */
export const ClothesRackSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1250 450" {...props}>
		<rect
			x="10"
			y="40"
			width="1230"
			height="370"
			rx="10"
			{...line}
			strokeWidth="6"
			strokeDasharray="30 18"
		/>
		<path d="M30 225H1220" stroke="currentColor" strokeWidth="20" strokeLinecap="round" />
		{[300, 450, 600, 750].map((x) => (
			<path key={x} d={`M${x} 90V360`} stroke="currentColor" strokeWidth="10" opacity="0.6" />
		))}
	</UIKit.SymbolFrame>
);

/** Ширма сверху: створки зигзагом */
export const RoomDividerSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1600 400" {...props}>
		<path
			d="M20 300L410 100L800 300L1190 100L1580 300"
			{...line}
			strokeWidth="28"
			strokeLinejoin="round"
			strokeLinecap="round"
		/>
	</UIKit.SymbolFrame>
);

/** Балконный столик сверху: круглый стол и два складных стула по бокам */
export const BistroSetSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 760" {...props}>
		<circle cx="600" cy="380" r="300" {...line} strokeWidth="14" />
		<rect x="20" y="200" width="240" height="360" rx="30" {...line} strokeWidth="10" />
		<rect x="940" y="200" width="240" height="360" rx="30" {...line} strokeWidth="10" />
	</UIKit.SymbolFrame>
);

/** Сушилка для белья сверху: рама и прутья вдоль */
export const DryingRackSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1300 550" {...props}>
		<rect x="10" y="10" width="1280" height="530" rx="20" {...line} strokeWidth="14" />
		<path
			d={Array.from({ length: 7 }, (_, i) => `M30 ${80 + i * 65}H1270`).join("")}
			stroke="currentColor"
			strokeWidth="6"
			opacity="0.6"
		/>
	</UIKit.SymbolFrame>
);

export interface FurnitureSymbolGroup {
	component: SymbolComponent;
	ids: readonly string[];
}

/** Каждый символ перечисляет все типы мебели, которые он рисует. */
export const FURNITURE_SYMBOL_GROUPS: readonly FurnitureSymbolGroup[] = [
	{
		component: BedSymbol,
		ids: [
			"fur-140",
			"bed-taupe",
			"bed-oak",
			"bed-single",
			"bed-scandi",
			"bed-wooden",
			"bed-single-wood",
			"bed-single-soft",
			"bed-single-storage",
			"bed-classic",
			"bed-king",
			"bed-luxury-2",
			"bed-luxury-3",
			"bed-luxury-4",
			"bed-pink",
			"bed-tufted",
			"bed-modern",
			"bed-kids-sofa",
			"bed-kids-wood",
		],
	},
	{ component: SofaSymbol, ids: ["sofa", "sofa-accordion"] },
	{ component: CornerSofaSymbol, ids: ["sofa-corner-black", "sofa-corner-orange"] },
	{
		component: CornerSofaLeftSymbol,
		ids: [
			"sofa-corner-leather",
			"sofa-corner-sectional",
			"sofa-corner-modular",
			"sofa-corner-brown",
		],
	},
	{ component: CurvedSofaSymbol, ids: ["sofa-corner-curved"] },
	{
		component: SofaFrontSymbol,
		ids: [
			"sofa-compact",
			"sofa-chesterfield",
			"sofa-midcentury",
			"sofa-scandi",
			"sofa-rounded",
			"sofa-dark",
			"sofa-olive",
			"sofa-kids",
			"sofa-beige",
			"sofa-brown",
			"sofa-leather",
			"sofa-classic",
			"sofa-cream",
			"sofa-fabric",
			"sofa-modular",
			"sofa-sectional",
		],
	},
	{
		component: ArmchairSymbol,
		ids: [
			"armchair",
			"armchair-wingback",
			"armchair-lounge",
			"armchair-rocking",
			"armchair-beige",
			"armchair-classic",
			"armchair-leather",
			"armchair-soft",
			"armchair-wood",
		],
	},
	{ component: BeanbagSymbol, ids: ["beanbag", "armchair-papasan"] },
	{
		component: ChairSymbol,
		ids: [
			"chair",
			"chair-soft",
			"chair-wood",
			"chair-plastic",
			"chair-modern",
			"chair-wooden",
			"chair-wooden-2",
			"chair-white",
			"chair-green",
			"chair-folding-wood",
		],
	},
	{
		component: OfficeChairSymbol,
		ids: [
			"chair-office",
			"chair-gaming",
			"chair-mesh",
			"chair-office-leather",
			"chair-office-dystopian",
			"chair-office-2",
			"chair-office-3",
		],
	},
	{
		component: DiningTableSymbol,
		ids: ["dining-table", "table-white", "table-oak", "table-glass"],
	},
	{ component: RoundTableSymbol, ids: ["table-round", "table-marble", "table-side"] },
	{ component: CoffeeTableSymbol, ids: ["coffee-table", "console-table"] },
	{
		component: PlainDeskSymbol,
		ids: ["desk-oak", "desk-standing", "desk-gaming", "desk-kids", "desk-modern"],
	},
	{
		component: DresserSymbol,
		ids: [
			"dresser",
			"shoe-cabinet",
			"dresser-tall",
			"sideboard",
			"sideboard-grey",
			"dresser-low",
			"toy-chest",
		],
	},
	{
		component: WardrobeSymbol,
		ids: ["wardrobe", "wardrobe-oak", "wardrobe-shelves", "wardrobe-wood"],
	},
	{
		component: TvStandSymbol,
		ids: ["tv-stand", "tv-stand-walnut", "tv-stand-white", "tv-stand-wood"],
	},
	{
		component: BookshelfSymbol,
		ids: ["bookshelf", "bookcase", "bookshelf-antique", "bookshelf-low"],
	},
	{ component: ShelfSymbol, ids: ["shelf-cube", "toy-storage"] },
	{
		component: FloorLampSymbol,
		ids: ["floor-lamp", "floor-lamp-tripod", "floor-lamp-gold"],
	},
	{ component: TurntableSymbol, ids: ["turntable", "turntable-cabinet"] },
	{ component: LoftBedSymbol, ids: ["bed-loft"] },
	{ component: HouseBedSymbol, ids: ["bed-house"] },
	{ component: CribSymbol, ids: ["bed-crib"] },
	{ component: LaptopSymbol, ids: ["laptop"] },
	{ component: MonitorSymbol, ids: ["monitor"] },
	{ component: PianoSymbol, ids: ["piano", "piano-antique"] },
	{
		component: NightstandSymbol,
		ids: ["nightstand", "nightstand-white", "nightstand-bedside", "nightstand-table"],
	},
	{ component: CoffeeMachineSymbol, ids: ["coffee-machine"] },
	{ component: ExerciseBikeSymbol, ids: ["exercise-bike"] },
	{ component: PoufSymbol, ids: ["pouf", "stool-kids"] },
	{ component: TvWallSymbol, ids: ["tv-wall"] },
	{ component: TvSymbol, ids: ["tv"] },
	{ component: PlaystationSymbol, ids: ["playstation"] },
	{ component: KitchenSetSymbol, ids: ["kitchen-set"] },
	{ component: FridgeSymbol, ids: ["fridge"] },
	{ component: DeskSymbol, ids: ["desk-computer"] },
	{ component: ChangingTableSymbol, ids: ["changing-table"] },
	{ component: DressingTableSymbol, ids: ["dressing-table", "dressing-table-wood"] },
	{ component: SlidingWardrobeSymbol, ids: ["wardrobe-sliding"] },
	{ component: DisplayCabinetSymbol, ids: ["display-cabinet"] },
	{ component: BarSetSymbol, ids: ["bar-set"] },
	{ component: BarStoolSymbol, ids: ["bar-stool", "bar-stool-wood"] },
	{ component: KitchenIslandSymbol, ids: ["kitchen-island"] },
	{ component: SideBySideFridgeSymbol, ids: ["fridge-side"] },
	{ component: MicrowaveSymbol, ids: ["microwave"] },
	{ component: CornerDeskSymbol, ids: ["desk-corner"] },
	{ component: GamingSetupSymbol, ids: ["gaming-setup"] },
	{ component: PcSymbol, ids: ["gaming-pc"] },
	{ component: ConsoleStandSymbol, ids: ["console-stand"] },
	{ component: SpeakerSymbol, ids: ["speaker-floor"] },
	{ component: RobotVacuumSymbol, ids: ["robot-vacuum"] },
	{ component: RockingHorseSymbol, ids: ["rocking-horse"] },
	{ component: TeepeeSymbol, ids: ["teepee"] },
	{ component: TreadmillSymbol, ids: ["treadmill"] },
	{ component: PunchingBagSymbol, ids: ["punching-bag"] },
	{ component: WallBarsSymbol, ids: ["wall-bars"] },
	{ component: BenchSymbol, ids: ["bench-ornate"] },
	{ component: ClothesRackSymbol, ids: ["clothes-rack"] },
	{ component: CoatStandSymbol, ids: ["coat-stand"] },
	{ component: NestingTablesSymbol, ids: ["coffee-table-nesting"] },
	{ component: ArcLampSymbol, ids: ["floor-lamp-arc"] },
	{ component: RoomDividerSymbol, ids: ["room-divider"] },
	{ component: StoveSymbol, ids: ["stove"] },
	{ component: DishwasherSymbol, ids: ["dishwasher"] },
	{ component: RangeHoodSymbol, ids: ["range-hood"] },
	{ component: KettleToasterSymbol, ids: ["kettle-toaster"] },
	{ component: TrashBinSymbol, ids: ["trash-bin"] },
	{ component: BenchBackSymbol, ids: ["bench-back"] },
	{ component: AirConditionerSymbol, ids: ["air-conditioner"] },
	{ component: SoundbarSymbol, ids: ["soundbar"] },
	{ component: AirPurifierSymbol, ids: ["air-purifier"] },
	{ component: KidsTableSymbol, ids: ["kids-table-set"] },
	{ component: HighChairSymbol, ids: ["high-chair", "high-chair-wood"] },
	{ component: WeightBenchSymbol, ids: ["weight-bench"] },
	{ component: DumbbellRackSymbol, ids: ["dumbbell-rack"] },
	{ component: RowingMachineSymbol, ids: ["rowing-machine"] },
	{ component: BicycleSymbol, ids: ["bicycle"] },
	{ component: BistroSetSymbol, ids: ["bistro-set"] },
	{ component: DryingRackSymbol, ids: ["drying-rack"] },
];

export const FURNITURE_SYMBOL_BY_ID = new Map(
	FURNITURE_SYMBOL_GROUPS.flatMap(({ component, ids }) =>
		ids.map((id) => [id, component] as const),
	),
);
